import React, { useEffect, useState, useCallback } from 'react';
import Skeleton from '../common/Skeleton';
import {
    Plus, Search, Settings,
    Calendar, Clock, Check, Save, CircleMinus, CircleCheck, Rocket, Edit2, Lock
} from 'lucide-react';
import Modal from '../common/Modal';
import Button from '../common/Button';
import FormControl from '../common/FormControl';
import Pagination from '../common/Pagination';
import Toast, { ToastMessage } from '../common/Toast';
import { confirmWarning, confirmActive } from '../common/confirm';
import DualListSelector, { DualListItem } from '../audits/procedure-types/common/DualListSelector';
import controlTestingService, {
    TestTemplate,
    TestTemplateCreatePayload,
    TemplateControl,
} from '../../services/controlTestingService';
import controlRegisterService, { ControlRegister } from '../../services/controlRegisterService';
import { userService } from '../../services/userService';
import GeographyPicker, { GeographySelection, isGeographyComplete } from '../common/GeographyPicker';
import extractApiError from '../../utils/apiError';
import NoDataFound from '../common/NoDataFound';
import StatusSwitch from '../common/StatusSwitch';
import TemplateCadenceTab from '../cadence/TemplateCadenceTab';
import TemplateProgramsTab from '../cadence/TemplateProgramsTab';
import { useSubscription } from '../../contexts/SubscriptionContext';
import ApprovalDot, { WorkflowState } from '../common/ApprovalDot';
import WorkflowModal from '../common/WorkflowModal';
import { usePermissions, usePermission } from '../../contexts/PermissionContext';
import { ValidatedInput, ValidatedTextarea } from '../../validation/ValidatedFields';
import { useFieldValidation } from '../../validation/useFieldValidation';
import { isUniqueLocal } from '../../validation/uniqueness';

const ACT_CAT_TEMPLATES = 'CTR_CAT_TEMPLATES';
const WF_KIND = 'ctr_cat_template';

// =============================================================================
// Step 1 — Identity modal
// =============================================================================

type IdentityForm = {
    id?: number;
    testing_code: string;
    name: string;
    testing_description?: string;
    is_active?: number;
};

const EMPTY_IDENTITY: IdentityForm = {
    testing_code: '',
    name: '',
    testing_description: '',
};

const IdentityModal = ({
    isOpen, onClose, initial, onSubmit, onConfigure, onRollout, toggling, onToggleActive, onError,
}: {
    isOpen: boolean;
    onClose: () => void;
    initial?: IdentityForm;
    onSubmit: (form: IdentityForm) => Promise<void>;
    onConfigure?: () => void;
    onRollout?: () => void;
    toggling?: boolean;
    onToggleActive?: () => Promise<void>;
    onError: (msg: string) => void;
}) => {
    const [formData, setFormData] = useState<IdentityForm>(initial || EMPTY_IDENTITY);
    const [submitting, setSubmitting] = useState(false);
    const vh = useFieldValidation();

    useEffect(() => {
        setFormData(initial || EMPTY_IDENTITY);
        setSubmitting(false);
        vh.reset();
    }, [initial, isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitting(true);
        const valid = vh.validateAll([
            { name: 'name', code: 109, value: formData.name, required: true },
            { name: 'testing_description', code: 110, value: formData.testing_description || '' },
        ]);
        if (!valid) { setSubmitting(false); return; }
        try {
            await onSubmit(formData);
        } catch (err: any) {
            onError(extractApiError(err, 'Failed to save template.'));
        } finally {
            setSubmitting(false);
        }
    };

    const isEdit = !!initial?.id;
    const isBusy = submitting || (toggling ?? false);

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="md">
            <Modal.Header>
                {isEdit
                    ? <>Edit "<span className="item-highlight">{initial!.name}</span>"</>
                    : 'Create CAT Template'}
            </Modal.Header>
            <Modal.Body>
                <form id="testing-identity-form" onSubmit={handleSubmit} className="flex flex-col gap-y-4" data-testid="MOD_control_testing_identity_form">
                    {/* Template code is auto-generated (CTTM-N) on approve — no manual entry. */}
                    <ValidatedInput
                        label="Template Name"
                        required
                        name="name"
                        code={109}
                        vh={vh}
                        value={formData.name}
                        onChange={e => setFormData({ ...formData, name: e.target.value })}
                        onAfterBlur={async (val) => {
                            if (!val) return;
                            const ok = isUniqueLocal(941, val, [], formData.id);
                            if (!ok) vh.setError('name', 'Template name already exists');
                        }}
                        data-testid="INP_control_testing_name"
                    />
                    <ValidatedTextarea
                        label="Description"
                        name="testing_description"
                        code={110}
                        vh={vh}
                        value={formData.testing_description || ''}
                        onChange={e => setFormData({ ...formData, testing_description: e.target.value })}
                        placeholder="What is this template for?"
                        data-testid="INP_control_testing_description"
                    />
                    {!isEdit && (
                        <p className="text-xs text-gray-500 italic">
                            After creating, reopen this template and click <span className="font-medium">Configure</span> to set cadence, due-date rule, and scope.
                        </p>
                    )}
                </form>
            </Modal.Body>
            <Modal.Footer className={isEdit ? 'app-modal-footer--split' : ''}>
                {isEdit && onToggleActive && (
                    <Button
                        variant={(initial!.is_active ?? 1) ? 'inactive-outline' : 'active-outline'}
                        icon={(initial!.is_active ?? 1) ? CircleMinus : CircleCheck}
                        onClick={onToggleActive}
                        disabled={isBusy}
                        data-testid="BTN_control_testing_toggle_active"
                    >
                        {(toggling ?? false) ? '...' : (initial!.is_active ?? 1) ? 'Inactive' : 'Active'}
                    </Button>
                )}
                <div style={{ display: 'flex', gap: 8 }}>
                    {isEdit && onConfigure && (
                        <Button
                            type="button"
                            variant="outline"
                            icon={Settings}
                            onClick={onConfigure}
                            disabled={isBusy}
                            data-testid="BTN_control_testing_configure"
                        >
                            Configure
                        </Button>
                    )}
                    {isEdit && onRollout && (
                        <Button
                            type="button"
                            variant="fill"
                            icon={Rocket}
                            onClick={onRollout}
                            disabled={isBusy}
                        >
                            Roll Out
                        </Button>
                    )}
                    <Button
                        type="submit"
                        form="testing-identity-form"
                        variant={isEdit ? 'update' : 'fill'}
                        icon={isEdit ? Check : Save}
                        disabled={isBusy}
                        data-testid="BTN_control_testing_submit"
                    >
                        {submitting
                            ? isEdit ? 'Updating...' : 'Submitting...'
                            : isEdit ? 'Update' : 'Submit'}
                    </Button>
                </div>
            </Modal.Footer>
        </Modal>
    );
};

// =============================================================================
// Step 2 — Configure modal: cadence, due-date rule, scope
// =============================================================================

type CatTab = 'details' | 'configure' | 'cadence' | 'programs';

const CAT_CONFIG_TABS: { key: CatTab; label: string }[] = [
    { key: 'details',   label: 'Basic Details' },
    { key: 'configure', label: 'Configurations' },
    { key: 'cadence',   label: 'Cadence' },
    { key: 'programs',  label: 'Rolled-out Programs' },
];

const ConfigureModal = ({
    isOpen, onClose, template, onSaved, onError,
}: {
    isOpen: boolean;
    onClose: () => void;
    template: TestTemplate;
    onSaved: () => void;
    onError: (msg: string) => void;
}) => {
    const [activeTab, setActiveTab] = useState<CatTab>('details');
    useEffect(() => { if (isOpen) setActiveTab('details'); }, [isOpen]);

    // RBAC (extent-only): in-context cadence tab is gated by SCH_CTR_CADENCE.
    const { check: checkPerm } = usePermissions();
    const canModifyCadence = checkPerm('SCH_CTR_CADENCE', 'view+modify').allowed;

    // Subscription: hide the in-context Cadence tab when the Cadence module is locked.
    const { isModuleLocked } = useSubscription();
    const catTabs = isModuleLocked('cadence')
        ? CAT_CONFIG_TABS.filter(t => t.key !== 'cadence')
        : CAT_CONFIG_TABS;

    const [toast, setToast] = useState<ToastMessage | null>(null);

    // Approved templates are frozen: no field edits, no toggle. Unlock via the
    // status dot's Reopen action.
    const isApproved = (template as any).workflow_state === 'approved';

    // ---- Basic Details tab ----
    const vhDetails = useFieldValidation();
    const [detailsForm, setDetailsForm] = useState({
        testing_code: template.testing_code,
        name: template.name || '',
        testing_description: template.testing_description || '',
    });
    const [localActive, setLocalActive] = useState(template.is_active ?? 1);
    const [submitting, setSubmitting] = useState(false);
    const [toggling, setToggling] = useState(false);
    const [editMode, setEditMode] = useState(false);

    const hasChanges = detailsForm.testing_code !== template.testing_code
        || detailsForm.name !== (template.name || '')
        || detailsForm.testing_description !== (template.testing_description || '');

    useEffect(() => {
        if (!isOpen) return;
        setDetailsForm({
            testing_code: template.testing_code,
            name: template.name || '',
            testing_description: template.testing_description || '',
        });
        setLocalActive(template.is_active ?? 1);
        setEditMode(false);
        vhDetails.reset();
    }, [isOpen, template]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleUpdateDetails = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitting(true); setToast(null);
        const valid = vhDetails.validateAll([
            { name: 'name', code: 109, value: detailsForm.name },
            { name: 'testing_description', code: 110, value: detailsForm.testing_description },
        ]);
        if (!valid) { setSubmitting(false); return; }
        try {
            await controlTestingService.update(template.id, {
                testing_code: detailsForm.testing_code,
                name: detailsForm.name,
                testing_description: detailsForm.testing_description,
            });
            onSaved();
            setEditMode(false);
            setToast({ type: 'success', message: 'Template updated successfully.' });
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Update failed') });
        } finally { setSubmitting(false); }
    };

    const handleToggle = async () => {
        const next = localActive ? 0 : 1;
        const confirmFn = next ? confirmActive : confirmWarning;
        const ok = await confirmFn({
            title: next ? 'Reactivate this template?' : 'Inactivate this template?',
            text: next ? 'It will appear in active lists again.' : 'It will be hidden from active lists.',
            confirmText: next ? 'Yes, Reactivate' : 'Yes, Inactivate',
        });
        if (!ok) return;
        setToggling(true);
        try {
            await controlTestingService.update(template.id, { is_active: next });
            setLocalActive(next);
            onSaved();
            setToast({ type: next ? 'success' : 'warning', message: next ? 'Reactivated.' : 'Inactivated.' });
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Toggle failed') });
        } finally { setToggling(false); }
    };

    // ---- Configurations tab ----
    const vhConfig = useFieldValidation();
    const [cadence, setCadence] = useState(template.cadence || '');
    const [dueRule, setDueRule] = useState(template.due_date_definition || '');
    const [allControls, setAllControls] = useState<ControlRegister[]>([]);
    const [selected, setSelected] = useState<DualListItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!isOpen || activeTab !== 'configure') return;
        let cancelled = false;
        setLoading(true);
        setCadence(template.cadence || '');
        setDueRule(template.due_date_definition || '');
        vhConfig.reset();
        Promise.all([
            controlRegisterService.getAll(),
            controlTestingService.listControls(template.id),
        ]).then(([controls, scope]) => {
            if (cancelled) return;
            // Only approved controls belong in the test-scope picker — getAll()
            // returns every workflow state (draft/submitted/rejected leaked in).
            setAllControls(controls.filter(c => c.workflow_state === 'approved'));
            setSelected(scope.map((c: TemplateControl) => ({
                id: c.id,
                title: c.control_code || c.control_name || `#${c.id}`,
                subtitle: c.control_type,
                description: c.control_name,
            })));
        }).catch(e => {
            if (!cancelled) setToast({ type: 'error', message: extractApiError(e, 'Failed to load configuration') });
        }).finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [isOpen, activeTab, template.id, template.cadence, template.due_date_definition]);

    const items: DualListItem[] = allControls.map(c => ({
        id: c.id,
        title: c.control_code || c.control_name || `#${c.id}`,
        subtitle: c.control_type,
        description: c.control_name,
        category: c.category_name,
    }));

    const handleSaveConfigure = async () => {
        // Approved templates are frozen — mirror the Basic Details tab lock and
        // every other workflow "approved" case: block the save and surface why
        // via a toast. Reopen via the status dot to edit.
        if (isApproved) {
            setToast({ type: 'error', message: 'Record is approved and locked. Reopen via the workflow button to edit.' });
            return;
        }
        const valid = vhConfig.validateAll([
            { name: 'cadence', code: 109, value: cadence },
            { name: 'due_rule', code: 109, value: dueRule },
        ]);
        if (!valid) return;
        setSaving(true); setToast(null);
        try {
            if (cadence !== (template.cadence || '') || dueRule !== (template.due_date_definition || '')) {
                await controlTestingService.update(template.id, {
                    cadence: cadence || undefined,
                    due_date_definition: dueRule || undefined,
                });
            }
            await controlTestingService.replaceScope(template.id, selected.map(s => Number(s.id)));
            onSaved();
            setToast({ type: 'success', message: 'Configuration saved.' });
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to save configuration') });
        } finally { setSaving(false); }
    };

    const anyBusy = submitting || toggling || saving;

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="lg" closeOnOverlayClick={false}>
            <Modal.Header>
                {isApproved && activeTab === 'details' ? (
                    <>Viewing (locked) "<span className="item-highlight">{template.name || ''}</span>"</>
                ) : (
                    <>Configure CAT Template — {template.code || template.testing_code || ''} · {template.name || ''}</>
                )}
            </Modal.Header>

            <Modal.Body>
                <div className="app-modal-tabs">
                    {catTabs.map(t => (
                        <button
                            key={t.key}
                            type="button"
                            onClick={() => setActiveTab(t.key)}
                            className={`app-modal-tab${activeTab === t.key ? ' app-modal-tab--active' : ''}`}
                            data-testid={`TAB_control_testing_configure_${t.key}`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>

                {activeTab === 'cadence' ? (
                    <TemplateCadenceTab
                        sourceTypeId={CAT_TEMPLATE_TYPE_ID}
                        sourceRecordId={template.id}
                        sourceTypeCode={CAT_TEMPLATE_TYPE_CODE}
                        sourceName={template.name || template.testing_code || `#${template.id}`}
                        sourceDisplayName={CAT_TEMPLATE_DISPLAY}
                        canModify={canModifyCadence}
                    />
                ) : activeTab === 'programs' ? (
                    <TemplateProgramsTab
                        sourceTypeId={CAT_TEMPLATE_TYPE_ID}
                        sourceRecordId={template.id}
                        sourceTypeCode={CAT_TEMPLATE_TYPE_CODE}
                    />
                ) : activeTab === 'details' ? (
                    <form id="test-template-details-form" onSubmit={handleUpdateDetails} className="flex flex-col gap-y-4 pt-4" data-testid="MOD_control_testing_details_form">
                        {isApproved && (
                            <div className="approved-banner" data-testid="BANNER_control_testing_locked">
                                <Lock size={14} /> Record is approved and locked. Reopen via the workflow button to edit.
                            </div>
                        )}
                        {/* Template Code — read-only in edit, mirroring the RAT pattern.
                            The list/header show `code` (auto-generated CTTM-N); fall back to
                            it so the field matches the row, then the legacy testing_code. */}
                        <FormControl.Input
                            label="Code"
                            value={template.code || template.testing_code || '—'}
                            disabled
                            data-testid="INP_control_testing_details_code"
                        />
                        <ValidatedInput
                            label="Template Name"
                            name="name"
                            code={109}
                            vh={vhDetails}
                            value={detailsForm.name}
                            onChange={e => setDetailsForm({ ...detailsForm, name: e.target.value })}
                            disabled={!editMode}
                            onAfterBlur={async (val) => {
                                if (!val || !editMode) return;
                                const ok = isUniqueLocal(941, val, [], template.id);
                                if (!ok) vhDetails.setError('name', 'Template name already exists');
                            }}
                            data-testid="INP_control_testing_details_name"
                        />
                        <ValidatedTextarea
                            label="Description"
                            name="testing_description"
                            code={110}
                            vh={vhDetails}
                            value={detailsForm.testing_description}
                            onChange={e => setDetailsForm({ ...detailsForm, testing_description: e.target.value })}
                            disabled={!editMode}
                            data-testid="INP_control_testing_details_description"
                        />
                    </form>
                ) : (
                    <>
                        <div className="grid grid-cols-2 gap-x-6 gap-y-4 mb-4 pt-4">
                            <ValidatedInput
                                label="Frequency"
                                name="cadence"
                                code={109}
                                vh={vhConfig}
                                value={cadence}
                                onChange={e => setCadence(e.target.value)}
                                disabled={isApproved}
                                placeholder="e.g. Quarterly"
                                data-testid="INP_control_testing_cadence"
                            />
                            <ValidatedInput
                                label="Due Date Rule"
                                name="due_rule"
                                code={109}
                                vh={vhConfig}
                                value={dueRule}
                                onChange={e => setDueRule(e.target.value)}
                                disabled={isApproved}
                                placeholder="e.g. Last day of quarter"
                                data-testid="INP_control_testing_due_rule"
                            />
                        </div>
                        <p className="text-sm font-semibold text-gray-700 mb-2">Scope — Controls covered</p>
                        <div style={{ height: '340px' }}>
                            {loading ? (
                                <div className="h-full flex items-center justify-center text-gray-500">Loading…</div>
                            ) : (
                                <DualListSelector
                                    availableItems={items}
                                    selectedItems={selected}
                                    onSelectionChange={setSelected}
                                    disabled={isApproved}
                                    titleLeft="Available Controls"
                                    titleRight="Controls in Template"
                                    searchPlaceholder="Search controls…"
                                    emptyMessageLeft="No more controls available"
                                    emptyMessageRight="No controls selected"
                                />
                            )}
                        </div>
                    </>
                )}
            </Modal.Body>

            <Modal.Footer className={activeTab === 'details' && !isApproved ? 'app-modal-footer--split' : ''}>
                {activeTab === 'details' && !isApproved ? (
                    <>
                        <Button
                            variant={localActive ? 'inactive-outline' : 'active-outline'}
                            icon={localActive ? CircleMinus : CircleCheck}
                            onClick={handleToggle}
                            disabled={anyBusy}
                            data-testid="BTN_control_testing_details_toggle"
                        >
                            {toggling ? '...' : localActive ? 'Inactive' : 'Active'}
                        </Button>
                        {editMode ? (
                            <Button
                                type="submit"
                                form="test-template-details-form"
                                variant="update"
                                icon={Check}
                                disabled={anyBusy || !hasChanges}
                                data-testid="BTN_control_testing_details_update"
                            >
                                {submitting ? 'Updating...' : 'Update'}
                            </Button>
                        ) : (
                            <Button
                                variant="fill"
                                icon={Edit2}
                                onClick={() => setEditMode(true)}
                                disabled={anyBusy}
                                data-testid="BTN_control_testing_details_edit"
                            >
                                Edit
                            </Button>
                        )}
                    </>
                ) : activeTab === 'configure' ? (
                    <Button
                        variant="update"
                        icon={Check}
                        onClick={handleSaveConfigure}
                        disabled={saving || loading}
                        className={isApproved ? 'opacity-50 cursor-not-allowed' : ''}
                        title={isApproved ? 'Record is approved and locked. Reopen via the workflow button to edit.' : undefined}
                        data-testid="BTN_control_testing_configure_save"
                    >
                        {saving ? 'Updating…' : 'Update'}
                    </Button>
                ) : null}
            </Modal.Footer>

            <Toast toast={toast} onClose={() => setToast(null)} />
        </Modal>
    );
};

// Migration 114 — UTL_entity_registry id for CONTROL_TEST_TEMPLATE.
const CAT_TEMPLATE_TYPE_ID = 34;
const CAT_TEMPLATE_TYPE_CODE = 'CONTROL_TEST_TEMPLATE';
const CAT_TEMPLATE_DISPLAY = 'Control Test Template';

// =============================================================================
// Step 3 — Roll Out modal: maker, checker, location, start/end date
// =============================================================================

const RolloutModal = ({
    isOpen, onClose, template, onRolledOut, onError,
}: {
    isOpen: boolean;
    onClose: () => void;
    template: TestTemplate;
    onRolledOut: () => void;
    onError: (msg: string) => void;
}) => {
    const [users, setUsers] = useState<{ id: number; name: string }[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [makerId, setMakerId] = useState<string>('');
    const [checkerId, setCheckerId] = useState<string>('');
    const [geography, setGeography] = useState<GeographySelection>({ mode: 'locations', location_ids: [] });
    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');

    useEffect(() => {
        if (!isOpen) return;
        let cancelled = false;
        setLoading(true);
        setMakerId('');
        setCheckerId('');
        setGeography({ mode: 'locations', location_ids: [] });
        setStartDate('');
        setEndDate('');
        userService.getUsersDropdown()
            .then(u => { if (!cancelled) setUsers(u.map(x => ({ id: x.id, name: x.name }))); })
            .catch(e => { if (!cancelled) onError(extractApiError(e, 'Failed to load users')); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
        // Only reset/reload when the modal opens. `onError` is an inline
        // callback recreated on every parent render (e.g. when a toast fires),
        // so including it here would re-run this effect and wipe the user's
        // in-progress inputs whenever an error toast is shown.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!makerId || !checkerId || !startDate || !endDate || !isGeographyComplete(geography)) {
            onError('Maker, checker, geography, start date and end date are required.');
            return;
        }
        if (endDate < startDate) {
            onError('End date must be on or after start date.');
            return;
        }
        setSaving(true);
        try {
            await controlTestingService.rollout(template.id, {
                maker_id: parseInt(makerId, 10),
                checker_id: parseInt(checkerId, 10),
                geography,
                start_date: startDate,
                end_date: endDate,
            });
            onRolledOut();
            onClose();
        } catch (err: any) {
            onError(extractApiError(err, 'Failed to roll out program.'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="md" closeOnOverlayClick={false}>
            <Modal.Header>
                Roll Out CAT — "<span className="item-highlight">{template.name || template.testing_code}</span>"
            </Modal.Header>
            <Modal.Body>
                <form id="testing-rollout-form" onSubmit={handleSubmit} className="flex flex-col gap-y-4">
                    {makerId !== '' && makerId === checkerId && (
                        <div className="warning-banner" role="status"
                             data-testid="WRN_rollout_same_maker_checker">
                            Maker and checker are the same person. This assessment will be performed and
                            approved by one individual — permitted, and recorded as self-reviewed.
                        </div>
                    )}
                    <FormControl.SearchSelect
                        label="Maker (executor)"
                        required
                        options={users.map(u => ({ value: u.id, label: u.name }))}
                        value={makerId}
                        onChange={v => setMakerId(v == null ? '' : String(v))}
                        disabled={loading}
                        placeholder="Select user…"
                        data-testid="SEL_testing_rollout_maker"
                    />
                    <FormControl.SearchSelect
                        label="Checker (approver)"
                        required
                        options={users.map(u => ({ value: u.id, label: u.name }))}
                        value={checkerId}
                        onChange={v => setCheckerId(v == null ? '' : String(v))}
                        disabled={loading}
                        placeholder="Select user…"
                        data-testid="SEL_testing_rollout_checker"
                    />
                    <div>
                        <label className="fg__label">Geography <span className="fg__required" aria-hidden="true">*</span></label>
                        <GeographyPicker value={geography} onChange={setGeography} disabled={loading} />
                    </div>
                    <div className="grid grid-cols-2 gap-x-4">
                        <FormControl.DatePicker
                            label="Start Date"
                            required
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                        />
                        <FormControl.DatePicker
                            label="End Date"
                            required
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                        />
                    </div>
                    <p className="text-xs text-gray-500 italic">
                        Roll-out creates one control test program per selected location, sharing the
                        same maker, checker and dates. The maker records Pass/Fail per
                        implementation; the checker approves the result.
                    </p>
                </form>
            </Modal.Body>
            <Modal.Footer>
                <Button
                    type="submit"
                    form="testing-rollout-form"
                    variant="fill"
                    icon={Rocket}
                    disabled={saving || loading}
                >
                    {saving ? 'Rolling out…' : 'Roll Out'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

// =============================================================================
// Main component
// =============================================================================

const ControlTesting = () => {
    const [templates, setTemplates] = useState<TestTemplate[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<'active' | 'inactive'>('active');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [isIdentityOpen, setIsIdentityOpen] = useState(false);
    const [isConfigureOpen, setIsConfigureOpen] = useState(false);
    const [isRolloutOpen, setIsRolloutOpen] = useState(false);
    const [editingIdentity, setEditingIdentity] = useState<IdentityForm | undefined>(undefined);
    const [activeTemplate, setActiveTemplate] = useState<TestTemplate | undefined>(undefined);
    const [toast, setToast] = useState<ToastMessage | null>(null);
    const [toggling, setToggling] = useState(false);
    const [workflowRow, setWorkflowRow] = useState<TestTemplate | null>(null);
    const { check } = usePermissions();
    const { allowed: canView, loading: viewLoading } = usePermission(ACT_CAT_TEMPLATES, 'view');
    const canModify = check(ACT_CAT_TEMPLATES, 'view+modify').allowed;

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const data = await controlTestingService.list();
            setTemplates(data);
        } catch (e: any) {
            // 403 = caller lacks view extent on this activity; render the empty
            // state silently instead of surfacing an RBAC denial as a toast.
            if (e?.response?.status === 403 || e?.status === 403) {
                setTemplates([]);
            } else {
                setToast({ type: 'error', message: extractApiError(e, 'Failed to load templates') });
            }
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { if (canView) refresh(); }, [refresh, canView]);

    useEffect(() => { setPage(1); }, [searchTerm, statusFilter, pageSize]);

    const filtered = templates.filter(t => {
        const active = (t.is_active ?? 1) === 1;
        if (statusFilter === 'active' ? !active : active) return false;
        if (!searchTerm) return true;
        const q = searchTerm.toLowerCase();
        return (
            (t.testing_code || '').toLowerCase().includes(q) ||
            (t.name || '').toLowerCase().includes(q) ||
            (t.testing_description || '').toLowerCase().includes(q)
        );
    });

    const totalRecords = filtered.length;
    const paginatedRows = filtered.slice((page - 1) * pageSize, page * pageSize);

    const handleCreate = () => {
        setEditingIdentity(undefined);
        setIsIdentityOpen(true);
    };

    const handleConfigureFromModal = () => {
        if (!activeTemplate) return;
        setIsConfigureOpen(true);
    };

    const handleRolloutFromModal = () => {
        if (!activeTemplate) return;
        if ((activeTemplate.control_count ?? 0) === 0) {
            setToast({ type: 'error', message: 'Template has no controls. Configure scope first.' });
            return;
        }
        setIsIdentityOpen(false);
        setIsRolloutOpen(true);
    };

    const handleToggleActive = async () => {
        if (!editingIdentity?.id) return;
        const current = editingIdentity.is_active ?? 1;
        const next = current ? 0 : 1;
        const confirmFn = next ? confirmActive : confirmWarning;
        const ok = await confirmFn({
            title: next ? 'Reactivate this template?' : 'Inactivate this template?',
            text: next ? 'It will appear in active lists again.' : 'It will be hidden from active lists.',
            confirmText: next ? 'Yes, Reactivate' : 'Yes, Inactivate',
        });
        if (!ok) return;
        setToggling(true);
        try {
            await controlTestingService.update(editingIdentity.id, { is_active: next });
            setEditingIdentity(prev => prev ? { ...prev, is_active: next } : prev);
            setToast({ type: next ? 'success' : 'warning', message: next ? 'Template reactivated.' : 'Template inactivated.' });
            await refresh();
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to update status.') });
        } finally {
            setToggling(false);
        }
    };

    const handleSubmitIdentity = async (form: IdentityForm) => {
        if (form.id) {
            await controlTestingService.update(form.id, {
                testing_code: form.testing_code,
                name: form.name,
                testing_description: form.testing_description,
            });
            setToast({ type: 'success', message: 'Template updated successfully.' });
        } else {
            const payload: TestTemplateCreatePayload = {
                testing_code: form.testing_code,
                name: form.name,
                testing_description: form.testing_description,
            };
            await controlTestingService.create(payload);
            setToast({ type: 'success', message: 'Template created successfully.' });
        }
        setIsIdentityOpen(false);
        await refresh();
    };

    if (!viewLoading && !canView) {
        return (
            <div className="flex flex-col h-full min-h-[300px] items-center justify-center">
                <NoDataFound title="Not authorised" message="You do not have access to 'CTR_CAT_TEMPLATES'." />
            </div>
        );
    }

    return (
        <div className="netbody-card flex flex-col h-full">
            <div className="netbody-header">
                {/* Status — iOS-style on/off switch (on = active) */}
                <StatusSwitch
                    value={statusFilter === 'inactive' ? 'INACTIVE' : 'ACTIVE'}
                    onChange={next => setStatusFilter(next === 'INACTIVE' ? 'inactive' : 'active')}
                    noun="templates"
                    testId="SEL_cat_template_status"
                />
                <div className="flex items-center gap-2 ml-auto">
                    <FormControl.Input
                        type="text"
                        placeholder="Search templates…"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        leadingIcon={<Search size={14} />}
                        className="filter-search"
                        data-testid="INP_control_testing_search"
                    />
                    {canModify && (
                        <Button variant="fill" icon={Plus} onClick={handleCreate} data-testid="BTN_control_testing_create">Create</Button>
                    )}
                </div>
            </div>

            <div className="netbody-content">
                <div className="scroll-container flex-1 overflow-hidden">
                    <div className="table-list">
                        <div className="table-list-scroll">
                            <table className="table-list-table">
                                <thead>
                                    <tr>
                                        <th className="table-list-th table-list-cell--dot" aria-label="Workflow status"></th>
                                        <th className="table-list-th table-list-cell--sno">S.No</th>
                                        <th className="table-list-th">Code</th>
                                        <th className="table-list-th">Template</th>
                                        <th className="table-list-th">Frequency</th>
                                        <th className="table-list-th">Controls</th>
                                        <th className="table-list-th table-list-th--right">Roll Out</th>
                                    </tr>
                                </thead>
                                <tbody aria-busy={loading} aria-live="polite">
                                    {loading && <Skeleton.TableRows cols={['dot', '3xs', 'sm', 'md', 'sm', 'pill', { width: 'sm', align: 'right' }]} />}
                                    {!loading && filtered.length === 0 && (
                                        <tr>
                                            <td colSpan={7} className="table-list-empty-cell">
                                                <NoDataFound
                                                    title={searchTerm ? 'No Matching Templates' : 'No Templates Yet'}
                                                    message={searchTerm ? 'No templates match your search.' : 'Click "Create" to add one.'}
                                                />
                                            </td>
                                        </tr>
                                    )}
                                    {!loading && paginatedRows.map((t, idx) => {
                                        const isConfigured = !!(t.cadence || t.due_date_definition || (t.control_count ?? 0) > 0);
                                        return (
                                            <tr
                                                key={t.id}
                                                className="table-list-row"
                                                onClick={() => { setActiveTemplate(t); setIsConfigureOpen(true); }}
                                                data-testid={`BTN_control_testing_row_${t.id}_open`}
                                            >
                                                <td
                                                    className="table-list-cell table-list-cell--dot"
                                                    onClick={(e) => { e.stopPropagation(); setWorkflowRow(t); }}
                                                    title="Workflow — view timeline & act"
                                                    data-testid={`BTN_control_testing_row_${t.id}_workflow`}
                                                >
                                                    <ApprovalDot
                                                        state={(((t as any).workflow_state) || 'draft') as WorkflowState}
                                                        onClick={() => setWorkflowRow(t)}
                                                        title="Workflow — view timeline & act"
                                                    />
                                                </td>
                                                <td className="table-list-cell table-list-cell--sno">{(page - 1) * pageSize + idx + 1}</td>
                                                <td className="table-list-cell">
                                                    {t.code
                                                        ? <span className="cell-code">{t.code}</span>
                                                        : <span className="text-muted text-xs">—</span>}
                                                </td>
                                                <td className="table-list-cell">
                                                    <span className="table-list-cell-title cell-name-lock">
                                                        {t.workflow_state === 'approved' && (
                                                            <Lock className="lock-icon lock-icon--sm" aria-label="Locked — approved" />
                                                        )}
                                                        {t.name || t.testing_code}
                                                    </span>
                                                    {t.testing_description && (
                                                        <span className="table-list-cell-sub">{t.testing_description}</span>
                                                    )}
                                                </td>
                                                <td className="table-list-cell">
                                                    <div className="flex flex-col gap-0.5 text-sm text-gray-600">
                                                        <span className="flex items-center gap-1">
                                                            <Calendar size={12} className="text-gray-400" />
                                                            {t.cadence || <span className="italic text-gray-400">not set</span>}
                                                        </span>
                                                        {t.due_date_definition && (
                                                            <span className="flex items-center gap-1">
                                                                <Clock size={12} className="text-gray-400" />
                                                                {t.due_date_definition}
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="table-list-cell">
                                                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${isConfigured ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>
                                                        {t.control_count ?? 0} controls
                                                    </span>
                                                </td>
                                                <td className="table-list-cell" style={{ textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                                                    {(() => {
                                                        const noControls = (t.control_count ?? 0) === 0;
                                                        return (
                                                    <Button
                                                        variant="secondary-outline"
                                                        icon={Rocket}
                                                        title={noControls ? 'Template has no controls — configure scope first' : 'Roll Out'}
                                                        style={noControls ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                                                        onClick={(e: React.MouseEvent) => {
                                                            e.stopPropagation();
                                                            if (noControls) {
                                                                setToast({ type: 'error', message: 'Template has no controls. Configure scope first.' });
                                                                return;
                                                            }
                                                            setActiveTemplate(t);
                                                            setIsRolloutOpen(true);
                                                        }}
                                                        data-testid={`BTN_control_testing_row_${t.id}_rollout`}
                                                    >
                                                        Rollout
                                                    </Button>
                                                        );
                                                    })()}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        <Pagination
                            page={page}
                            pageSize={pageSize}
                            totalRecords={totalRecords}
                            onPageChange={setPage}
                            onPageSizeChange={setPageSize}
                            recordLabel="templates"
                        />
                    </div>
                </div>
            </div>

            <Toast toast={toast} onClose={() => setToast(null)} />

            <IdentityModal
                isOpen={isIdentityOpen}
                onClose={() => setIsIdentityOpen(false)}
                initial={editingIdentity}
                onSubmit={handleSubmitIdentity}
                onConfigure={editingIdentity?.id ? handleConfigureFromModal : undefined}
                onRollout={editingIdentity?.id ? handleRolloutFromModal : undefined}
                toggling={toggling}
                onToggleActive={editingIdentity?.id ? handleToggleActive : undefined}
                onError={(msg) => setToast({ type: 'error', message: msg })}
            />
            {activeTemplate && (
                <ConfigureModal
                    isOpen={isConfigureOpen}
                    onClose={() => setIsConfigureOpen(false)}
                    template={activeTemplate}
                    onSaved={refresh}
                    onError={(msg) => setToast({ type: 'error', message: msg })}
                />
            )}
            {activeTemplate && (
                <RolloutModal
                    isOpen={isRolloutOpen}
                    onClose={() => setIsRolloutOpen(false)}
                    template={activeTemplate}
                    onRolledOut={() => {
                        setToast({ type: 'success', message: 'Test program rolled out.' });
                        refresh();
                    }}
                    onError={(msg) => setToast({ type: 'error', message: msg })}
                />
            )}
            <WorkflowModal
                isOpen={!!workflowRow}
                onClose={() => setWorkflowRow(null)}
                kind={WF_KIND}
                id={workflowRow?.id ?? null}
                onChanged={refresh}
            />
        </div>
    );
};

export default ControlTesting;
